import { useState, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { PhoneInput, isValidPhoneNumber } from '@/components/ui/phone-input';
import { useEditarCliente } from '@/hooks/useClientes';
import { useOrdensServicoAbertas, useAtualizarEnderecoOS } from '@/hooks/useOrdensServicoAbertas';
import { useServicosAtivos } from '@/hooks/useServicos';
import { useAuthContext } from '@/contexts/AuthContext';
import { Loader2, Save, AlertTriangle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

type ClienteComEndereco = {
  id: string;
  nome: string | null;
  telefone: string;
  email: string | null;
  servico_interesse: string;
  endereco_rua: string | null;
  endereco_numero: string | null;
  endereco_complemento: string | null;
  endereco_codigo_postal: string | null;
  endereco_cidade: string | null;
  endereco_regiao?: string | null;
  endereco_pais: string | null;
  observacoes?: string | null;
};

interface ClienteEditFormProps {
  cliente: ClienteComEndereco;
}

interface FormData {
  nome: string;
  telefone: string;
  email: string;
  servico_interesse: string;
  endereco_rua: string;
  endereco_numero: string;
  endereco_complemento: string;
  endereco_codigo_postal: string;
  endereco_cidade: string;
  endereco_regiao: string;
  endereco_pais: string;
  observacoes: string;
}

// CAMPO_LABELS keys for i18n - resolved at render time via getCampoLabels()
const CAMPO_LABEL_KEYS: Record<string, string> = {
  nome: 'clients.form.name',
  telefone: 'clients.form.phone',
  email: 'clients.form.email',
  servico_interesse: 'clients.form.serviceOfInterest',
  endereco_rua: 'clients.form.street',
  endereco_numero: 'clients.form.number',
  endereco_complemento: 'clients.form.complement',
  endereco_codigo_postal: 'clients.form.postalCode',
  endereco_cidade: 'clients.form.city',
  endereco_regiao: 'clients.form.region',
  endereco_pais: 'clients.form.country',
  observacoes: 'clientPotential.notes',
};


export function ClienteEditForm({ cliente }: ClienteEditFormProps) {
  const { t } = useTranslation();
  const { profile, organization } = useAuthContext();
  const editarCliente = useEditarCliente();
  const { data: servicosAtivos } = useServicosAtivos();
  const { data: osAbertas } = useOrdensServicoAbertas(cliente.id);
  const atualizarEnderecoOS = useAtualizarEnderecoOS();
  const [dialogAtualizarOS, setDialogAtualizarOS] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<FormData | null>(null);
  const [formData, setFormData] = useState<FormData>({
    nome: cliente.nome || '',
    telefone: cliente.telefone || '',
    email: cliente.email || '',
    servico_interesse: cliente.servico_interesse,
    endereco_rua: cliente.endereco_rua || '',
    endereco_numero: cliente.endereco_numero || '',
    endereco_complemento: cliente.endereco_complemento || '',
    endereco_codigo_postal: cliente.endereco_codigo_postal || '',
    endereco_cidade: cliente.endereco_cidade || '',
    endereco_regiao: cliente.endereco_regiao || '',
    endereco_pais: cliente.endereco_pais || '',
    observacoes: cliente.observacoes || '',
  });

  const [erros, setErros] = useState<{ telefone?: string; email?: string }>({});

  // Reset form when cliente changes
  useEffect(() => {
    setFormData({
      nome: cliente.nome || '',
      telefone: cliente.telefone || '',
      email: cliente.email || '',
      servico_interesse: cliente.servico_interesse,
      endereco_rua: cliente.endereco_rua || '',
      endereco_numero: cliente.endereco_numero || '',
      endereco_complemento: cliente.endereco_complemento || '',
      endereco_codigo_postal: cliente.endereco_codigo_postal || '',
      endereco_cidade: cliente.endereco_cidade || '',
      endereco_regiao: cliente.endereco_regiao || '',
      endereco_pais: cliente.endereco_pais || '',
      observacoes: cliente.observacoes || '',
    });
    setErros({});
  }, [cliente]);

  const camposAlterados = useMemo(() => {
    const alterados: string[] = [];
    
    if (formData.nome !== (cliente.nome || '')) alterados.push('nome');
    if (formData.telefone !== (cliente.telefone || '')) alterados.push('telefone');
    if (formData.email !== (cliente.email || '')) alterados.push('email');
    if (formData.servico_interesse !== cliente.servico_interesse) alterados.push('servico_interesse');
    if (formData.endereco_rua !== (cliente.endereco_rua || '')) alterados.push('endereco_rua');
    if (formData.endereco_numero !== (cliente.endereco_numero || '')) alterados.push('endereco_numero');
    if (formData.endereco_complemento !== (cliente.endereco_complemento || '')) alterados.push('endereco_complemento');
    if (formData.endereco_codigo_postal !== (cliente.endereco_codigo_postal || '')) alterados.push('endereco_codigo_postal');
    if (formData.endereco_cidade !== (cliente.endereco_cidade || '')) alterados.push('endereco_cidade');
    if (formData.endereco_regiao !== (cliente.endereco_regiao || '')) alterados.push('endereco_regiao');
    if (formData.endereco_pais !== (cliente.endereco_pais || '')) alterados.push('endereco_pais');
    if (formData.observacoes !== (cliente.observacoes || '')) alterados.push('observacoes');

    return alterados;
  }, [formData, cliente]);

  const temAlteracoes = camposAlterados.length > 0;
  
  // Verificar se campos de endereço foram alterados
  const camposEndereco = ['endereco_rua', 'endereco_numero', 'endereco_complemento', 'endereco_codigo_postal', 'endereco_cidade', 'endereco_regiao', 'endereco_pais'];
  const enderecoAlterado = camposAlterados.some(c => camposEndereco.includes(c));

  const validar = () => {
    const novosErros: { telefone?: string; email?: string } = {};
    
    if (!formData.telefone) {
      novosErros.telefone = t('common.validation.phoneRequired');
    } else if (!isValidPhoneNumber(formData.telefone)) {
      novosErros.telefone = t('common.validation.invalidPhone');
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      novosErros.email = t('common.validation.invalidEmail');
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  // Função para salvar os dados do cliente
  const salvarCliente = (atualizarOS: boolean = false) => {
    const camposAlteradosLabels = camposAlterados.map(c => t(CAMPO_LABEL_KEYS[c] || c));
    
    editarCliente.mutate({
      id: cliente.id,
      dados: {
        nome: formData.nome.trim() || null,
        telefone: formData.telefone, // Já vem formatado em E.164
        email: formData.email.trim() || null,
        servico_interesse: formData.servico_interesse,
        endereco_rua: formData.endereco_rua.trim() || null,
        endereco_numero: formData.endereco_numero.trim() || null,
        endereco_complemento: formData.endereco_complemento.trim() || null,
        endereco_codigo_postal: formData.endereco_codigo_postal.trim() || null,
        endereco_cidade: formData.endereco_cidade.trim() || null,
        endereco_pais: formData.endereco_pais.trim() || null,
        observacoes: formData.observacoes.trim() || null,
      },
      camposAlterados: camposAlteradosLabels,
      profileId: profile?.id,
    }, {
      onSuccess: async () => {
        // Se optou por atualizar as OS, fazer isso agora
        if (atualizarOS && osAbertas && osAbertas.length > 0) {
          try {
            await atualizarEnderecoOS(cliente.id, {
              rua: formData.endereco_rua.trim() || null,
              numero: formData.endereco_numero.trim() || null,
              complemento: formData.endereco_complemento.trim() || null,
              codigo_postal: formData.endereco_codigo_postal.trim() || null,
              cidade: formData.endereco_cidade.trim() || null,
              regiao: formData.endereco_regiao.trim() || null,
              pais: formData.endereco_pais.trim() || null,
            });
            toast({
              title: t('clients.updateOS.osUpdated'),
              description: t('clients.updateOS.osUpdatedDescription', { count: osAbertas.length }),
            });
          } catch (error) {
            toast({
              title: t('clients.updateOS.osUpdateError'),
              description: t('clients.updateOS.osUpdateErrorDescription'),
              variant: 'destructive',
            });
          }
        }
        setPendingFormData(null);
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validar() || !temAlteracoes) return;

    // Se tem OS abertas e o endereço foi alterado, perguntar ao usuário
    if (enderecoAlterado && osAbertas && osAbertas.length > 0) {
      setPendingFormData(formData);
      setDialogAtualizarOS(true);
      return;
    }

    // Caso contrário, salvar normalmente
    salvarCliente(false);
  };

  const handleConfirmarAtualizarOS = (atualizarOS: boolean) => {
    setDialogAtualizarOS(false);
    salvarCliente(atualizarOS);
  };

  const updateField = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 [&_input]:rounded-full">
      {/* Informações Básicas */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('clients.form.basicInfo')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="nome">{t('clients.form.name')}</Label>
              <Input
                id="nome"
                placeholder={t('clients.form.namePlaceholder')}
                value={formData.nome}
                onChange={(e) => updateField('nome', e.target.value)}
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="telefone">{t('clients.form.phone')} *</Label>
              <PhoneInput
                value={formData.telefone}
                onChange={(value) => updateField('telefone', value)}
                defaultCountry="PT"
                placeholder="912 345 678"
                error={!!erros.telefone}
                className="[&_input]:!rounded-s-none [&_input]:!rounded-e-md"
              />
              {erros.telefone && (
                <p className="text-sm text-destructive">{erros.telefone}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="email">{t('clients.form.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@exemplo.com"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                maxLength={255}
                className={erros.email ? 'border-destructive' : ''}
              />
              {erros.email && (
                <p className="text-sm text-destructive">{erros.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="servico">{t('clients.form.serviceOfInterest')}</Label>
              <Select
                value={formData.servico_interesse}
                onValueChange={(v) => updateField('servico_interesse', v)}
              >
                <SelectTrigger id="servico" className="rounded-full">
                  <SelectValue placeholder={t('clients.form.selectService')} />
                </SelectTrigger>
                <SelectContent>
                  {(servicosAtivos ?? []).map((s) => (
                    <SelectItem key={s.id} value={s.nome}>
                      {s.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Endereço */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('clients.form.address')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Linha 1: Rua + Número */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="endereco_rua">{t('clients.form.street')}</Label>
              <Input
                id="endereco_rua"
                placeholder="Rua Augusta"
                value={formData.endereco_rua}
                onChange={(e) => updateField('endereco_rua', e.target.value)}
                maxLength={200}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endereco_numero">{t('clients.form.number')}</Label>
              <Input
                id="endereco_numero"
                placeholder="145"
                value={formData.endereco_numero}
                onChange={(e) => updateField('endereco_numero', e.target.value)}
                maxLength={20}
              />
            </div>
          </div>

          {/* Linha 2: Complemento */}
          <div className="space-y-2">
            <Label htmlFor="endereco_complemento">{t('clients.form.complement')}</Label>
            <Input
              id="endereco_complemento"
              placeholder="Apartamento, andar, bloco..."
              value={formData.endereco_complemento}
              onChange={(e) => updateField('endereco_complemento', e.target.value)}
              maxLength={100}
            />
          </div>

          {/* Linha 3: Código Postal + Cidade */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="endereco_codigo_postal">{t('clients.form.postalCode')}</Label>
              <Input
                id="endereco_codigo_postal"
                placeholder="0000-000"
                value={formData.endereco_codigo_postal}
                onChange={(e) => updateField('endereco_codigo_postal', e.target.value)}
                maxLength={20}
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="endereco_cidade">{t('clients.form.city')}</Label>
              <Input
                id="endereco_cidade"
                placeholder="Lisboa"
                value={formData.endereco_cidade}
                onChange={(e) => updateField('endereco_cidade', e.target.value)}
                maxLength={100}
              />
            </div>
          </div>

          {/* Linha 4: Região + País */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="endereco_regiao">{t('clients.form.region')}</Label>
              <Input
                id="endereco_regiao"
                placeholder="Distrito de Lisboa"
                value={formData.endereco_regiao}
                onChange={(e) => updateField('endereco_regiao', e.target.value)}
                maxLength={100}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endereco_pais">{t('clients.form.country')}</Label>
              <Input
                id="endereco_pais"
                placeholder={organization?.pais ?? ''}
                value={formData.endereco_pais}
                onChange={(e) => updateField('endereco_pais', e.target.value)}
                maxLength={100}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Observações */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {t('clientPotential.notes')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={formData.observacoes}
            onChange={(e) => updateField('observacoes', e.target.value)}
            placeholder={t('clientPotential.notesPlaceholder')}
            rows={5}
          />
        </CardContent>
      </Card>

      {/* Botão Salvar */}
      <div className="flex justify-end">
        <button
          type="submit"
          className="ow-btn ow-btn--primary min-w-32"
          disabled={!temAlteracoes || editarCliente.isPending}
        >
          {editarCliente.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {t('common.saveChanges')}
        </button>
      </div>

      {temAlteracoes && (
        <p className="text-sm text-muted-foreground text-right">
          {t('clients.form.fieldsChanged', { fields: camposAlterados.map(c => t(CAMPO_LABEL_KEYS[c] || c)).join(', ') })}
        </p>
      )}

      {/* Dialog de confirmação para atualizar OS abertas */}
      <AlertDialog open={dialogAtualizarOS} onOpenChange={setDialogAtualizarOS}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              {t('clients.updateOS.title')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                <Trans
                  i18nKey="clients.updateOS.description"
                  values={{ count: osAbertas?.length || 0 }}
                  components={{ strong: <strong /> }}
                />
              </span>
              <span className="block">{t('clients.updateOS.question')}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleConfirmarAtualizarOS(false)}>
              {t('clients.updateOS.keep')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => handleConfirmarAtualizarOS(true)}>
              {t('clients.updateOS.updateAll')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
